
/**
 * Module dependencies.
 */

import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControl, FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { ReactNode } from 'react';
import { color, media } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import { useField, useFormState } from '@seegno/react-forms';
import styled, { css } from 'styled-components';

/**
 * Export `domainStates`.
 */

export const domainStates = {
  available: 'available',
  checking: 'checking',
  unavailable: 'unavailable',
  unknown: 'unknown'
};

/**
 * `Status` type.
 */

type Status = keyof typeof domainStates;

/**
 * `Props` type.
 */

type Props = {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  domain: ReactNode;
  hasErrorRule?: boolean;
  hasIcon?: boolean;
  helpText?: ReactNode;
  id?: string;
  isFloating?: boolean;
  label: string;
  name: string;
  onChange?: (value: string | null | undefined) => void;
  placeholder?: string;
  status?: Status;
  type?: 'error' | 'warning';
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)<{
  disabled: boolean;
  hasError: boolean;
  hasErrorRule: boolean;
  hasValue: boolean;
  status?: Status;
}>`
  column-gap: 16px;

  ${switchProp('status', {
    [domainStates.available]: css`
      &,
      &:hover {
        ${ifProp({ disabled: false, hasError: false, hasValue: true }, css`
          border-color: ${color('success500')};
        `)}
      }
    `,
    [domainStates.checking]: css`
      &,
      &:hover {
        ${ifProp({ disabled: false, hasValue: true }, css`
          border-color: ${color('warning500')};
        `)}
      }
    `,
    [domainStates.unavailable]: css`
      &,
      &:hover {
        ${ifProp({ disabled: false, hasValue: true }, css`
          border-color: ${color('danger500')};
        `)}
      }
    `,
    [domainStates.unknown]: css`
      &,
      &:hover {
        ${ifProp({ hasErrorRule: true }, css`
          border-color: ${color('warning500')};
        `)}
      }
    `
  })}
`;

/**
 * `StyledFormControl` styled component.
 */

const StyledFormControl = styled(FormControl)<{ hasErrorColor: boolean; hasFocus: boolean }>`
  flex: 1;
  padding-right: 0;

  ${ifProp({ hasErrorColor: true, hasFocus: false }, css`
    color: ${color('danger')};
  `, css`
    color: inherit;
  `)}

  ${media.min('md')`
    padding-right: 0;
  `}
`;

/**
 * `Domain` styled component.
 */

const Domain = styled.div<{ disabled: boolean }>`
  align-items: center;
  color: ${color('gray500')};
  display: flex;
  padding-right: 12px;
  position: relative;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}
`;

/**
 * Export `DomainInput` component.
 */

export function DomainInput(props: Props): JSX.Element {
  const {
    className,
    disabled,
    domain,
    hasErrorRule = false,
    hasIcon,
    helpText,
    id,
    isFloating,
    label,
    name,
    onChange,
    status,
    type,
    ...rest
  } = props;

  const { error, meta, value = '', ...fieldProps } = useField(name);
  const { active, touched } = meta || {};
  const { alreadySubmittted, isSubmitting } = useFormState();
  const hasValue = Boolean(value);
  const hasError = !!error && alreadySubmittted;
  const isDisabled = disabled || isSubmitting;

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
      hasAlert={hasError || !!helpText}
    >
      <StyledFormControlWrapper
        disabled={isDisabled}
        hasError={hasError}
        hasErrorRule={hasErrorRule}
        hasFocus={active}
        hasValue={active || hasValue && !touched}
        status={status}
      >
        <Label
          disabled={isDisabled}
          htmlFor={id ?? name}
          isFloating={isFloating ?? (hasValue || active)}
        >
          {label}
        </Label>

        <StyledFormControl
          {...rest}
          {...fieldProps}
          disabled={isDisabled}
          hasError={hasError}
          hasErrorColor={hasError && type !== 'warning'}
          hasFocus={active}
          id={id ?? name}
          name={name}
          onChange={event => {
            const value = event.target.value;

            fieldProps.onChange(value);

            if (onChange) {
              onChange(value);
            }
          }}
          type={'text'}
          value={value}
        />

        <Domain disabled={isDisabled}>
          {domain}
        </Domain>
      </StyledFormControlWrapper>

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        hasIcon={hasIcon}
        isOpen={hasError}
        name={name}
        type={type}
      />
    </FormGroup>
  );
}
